<template>
  <div class="container py-3">
    <!-- Page Header -->
    <div class="page-header">
      <h2>{{ $t('salesOrder.title') }}</h2>
      <button class="btn btn-primary" @click="openAddModal">{{ $t('salesOrder.add') }}</button>
    </div>

    <!-- Search -->
    <div class="search-wrapper mb-3">
      <span class="search-icon">🔍</span>
      <input v-model="search" type="text" class="form-control" placeholder="Search sales orders..." />
    </div>

    <!-- Sales Orders Table -->
    <div class="table-card">
      <table class="premium-table table">
        <thead>
          <tr>
            <th>{{ $t('common.id') }}</th>
            <th>{{ $t('salesOrder.customer') }}</th>
            <th>{{ $t('common.status') }}</th>
            <th>{{ $t('common.items') }}</th>
            <th>Total</th>
            <th>{{ $t('common.actions') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="orders.length === 0">
            <td colspan="5">
              <div class="empty-state">
                <div class="empty-icon">🛒</div>
                <p>No sales orders found</p>
              </div>
            </td>
          </tr>
          <tr v-for="so in orders" :key="so.id">
            <td><strong class="text-primary">SO-{{ so.id }}</strong></td>
            <td>{{ so.customer_name }}</td>
            <td>
              <span class="status-badge"
                :class="{
                  'status-pending':  so.status === 'PENDING',
                  'status-shipped':  so.status === 'SHIPPED',
                  'status-returned': so.status === 'RETURNED',
                  'status-voided':   so.status === 'VOIDED',
                  'status-draft':    !['PENDING','SHIPPED','RETURNED','VOIDED'].includes(so.status)
                }">
                {{ so.status }}
              </span>
            </td>
            <td>
              <ul class="item-list" v-if="so.lines && so.lines.length">
                <li v-for="line in so.lines" :key="line.id">
                  {{ line.product_name || getProductName(line.product) }}
                  &times; <strong>{{ line.qty_ordered }}</strong>
                  <span class="item-price">@ {{ fmt(line.unit_price) }} = <strong>{{ fmt(line.line_total) }}</strong></span>
                  <span v-if="line.cogs != null" class="cogs-badge" :title="'COGS: ' + fmt(line.cogs)">COGS: {{ fmt(line.cogs) }}</span>
                </li>
              </ul>
              <span v-else class="text-muted" style="font-size:0.8rem;">{{ $t('common.noItems') }}</span>
            </td>
            <td>
              <strong class="order-total">{{ fmt(so.total_amount) }}</strong>
              <div v-if="so.total_cogs > 0" class="cogs-summary" title="Cost of Goods Sold">
                COGS: {{ fmt(so.total_cogs) }}
              </div>
            </td>
            <td>
              <button v-if="so.status === 'PENDING'" class="btn-action btn-action-success"
                      @click="shipSO(so)">🚚 {{ $t('salesOrder.ship') }}</button>
              <button v-if="so.status === 'SHIPPED'" class="btn-action btn-action-danger"
                      @click="returnSO(so)">↩️ {{ $t('salesOrder.return') }}</button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Pagination -->
    <MainPagination
      v-model:currentPage="currentPage"
      v-model:pageSize="pageSize"
      :total="totalRecords"
    />

    <!-- Add/Edit Modal – nested lines -->
    <div class="modal fade" ref="modalEl" tabindex="-1">
      <div class="modal-dialog modal-lg">
        <div class="modal-content">
          <form @submit.prevent="saveSO">
            <div class="modal-header">
              <h5 class="modal-title">{{ isEdit ? $t('salesOrder.add') : $t('salesOrder.add') }}</h5>
              <button type="button" class="btn-close" @click="hideModal"></button>
            </div>
            <div class="modal-body">
              <div class="mb-3">
                <label>{{ $t('salesOrder.customer') }}</label>
                <select v-model="form.customer" class="form-select" required>
                  <option v-for="c in customers" :key="c.id" :value="c.id">{{ c.name }}</option>
                </select>
              </div>
              <div class="mb-3">
                <label>{{ $t('salesOrder.warehouse') }}</label>
                <select v-model="form.warehouse" class="form-select" required>
                  <option v-for="w in warehouses" :key="w.id" :value="w.id">{{ w.name }}</option>
                </select>
              </div>

              <h6>{{ $t('salesOrder.items') }}</h6>
              <div class="table-card-sm">
                <table class="premium-table table">
                  <thead>
                    <tr>
                      <th>{{ $t('salesOrder.product') }}</th>
                      <th>{{ $t('salesOrder.qty') }}</th>
                      <th>{{ $t('salesOrder.unitPrice') }}</th>
                      <th>Subtotal</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(line,idx) in form.lines" :key="idx">
                      <td>
                        <select v-model="line.product" class="form-select form-select-sm" required>
                          <option v-for="p in products" :key="p.id" :value="p.id">{{ p.name }}</option>
                        </select>
                      </td>
                      <td><input v-model.number="line.qty_ordered" type="number" class="form-control form-control-sm" min="1" required></td>
                      <td><input v-model.number="line.unit_price" type="number" step="0.01" min="0.01" class="form-control form-control-sm" required></td>
                      <td class="text-end" style="white-space:nowrap; font-weight:600;">
                        {{ fmt((line.qty_ordered || 0) * (line.unit_price || 0)) }}
                      </td>
                      <td><button type="button" class="btn-action btn-action-danger" @click="form.lines.splice(idx,1)">✕</button></td>
                    </tr>
                  </tbody>
                  <tfoot v-if="form.lines.length">
                    <tr style="border-top: 2px solid var(--bs-border-color, #e2e8f0);">
                      <td colspan="3" class="text-end" style="font-weight:700; padding: 0.6rem 1rem;">Order Total:</td>
                      <td class="text-end text-primary" style="font-weight:700; padding: 0.6rem 1rem;">
                        {{ fmt(form.lines.reduce((s,l) => s + (l.qty_ordered||0)*(l.unit_price||0), 0)) }}
                      </td>
                      <td></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
              <button type="button" class="btn btn-sm btn-outline-primary" @click="form.lines.push({product:'', qty_ordered:1, unit_price:''})">{{ $t('salesOrder.addLine') }}</button>
            </div>
            <div class="modal-footer">
              <button type="button" class="btn btn-secondary" @click="hideModal">{{ $t('common.cancel') }}</button>
              <button type="submit" class="btn btn-primary">{{ $t('common.save') }}</button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, watch, onMounted } from 'vue'
import api, { shipSalesOrder, returnSalesOrder } from '@/api/axios'
import * as bootstrap from 'bootstrap'
import { useI18n } from 'vue-i18n'
import MainPagination from '../components/MainPagination.vue'

const { t } = useI18n()


/* Currency formatter */
const fmt = (val) => {
  if (val == null || val === '') return '—'
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(Number(val))
}

/* ---------- reactive ---------- */
const orders     = ref([])
const search     = ref('')
const currentPage = ref(1)
const pageSize   = ref(10)
const totalRecords = ref(0)
const customers  = ref([]); const warehouses = ref([]); const products = ref([])
const form       = ref({ customer:'', warehouse:'', lines:[] })
const isEdit     = ref(false)

/* ---------- template ref ---------- */
const modalEl    = ref(null)
let modalInstance = null

/* ---------- life-cycle ---------- */
onMounted(async () => {
  await fetchData()
  modalInstance = new bootstrap.Modal(modalEl.value)
})

/* ---------- data ---------- */
const fetchData = async () => {
  try {
    const [soRes, custRes, whRes, prodRes] = await Promise.all([
      api.get('/sales-orders/', {
        params: {
          page: currentPage.value,
          page_size: pageSize.value,
          search: search.value
        }
      }),
      api.get('/customers/?pagination=false'),
      api.get('/warehouses/?pagination=false'),
      api.get('/products/?pagination=false')
    ])
    orders.value    = soRes.data.results
    totalRecords.value = soRes.data.count
    customers.value = custRes.data
    warehouses.value= whRes.data
    products.value  = prodRes.data
  } catch (err) {
    console.error(err)
  }
}

let searchTimeout = null
watch(search, () => {
  currentPage.value = 1
  clearTimeout(searchTimeout)
  searchTimeout = setTimeout(() => {
    fetchData()
  }, 300)
})

watch([currentPage, pageSize], () => {
  fetchData()
})

/* helper to resolve product name by ID */
const getProductName = (productId) => {
  const p = products.value.find(p => p.id === productId)
  return p ? p.name : `Product #${productId}`
}

/* ---------- modal ---------- */
const hideModal = () => modalInstance.hide()
const openAddModal = () => {
  isEdit.value = false
  form.value   = { customer:'', warehouse:'', lines:[{}] }
  modalInstance.show()
}

/* ---------- CRUD ---------- */
const saveSO = async () => {
  /* validate lines */
  for (const l of form.value.lines) {
    if (!l.product || !l.qty_ordered || l.qty_ordered <= 0) {
      alert('Each line must have a product and a quantity greater than 0.')
      return
    }
    if (!l.unit_price || Number(l.unit_price) <= 0) {
      alert('Each line must have a unit price greater than 0.')
      return
    }
  }

  const payload = {
    customer:  Number(form.value.customer),
    warehouse: Number(form.value.warehouse),
    lines: form.value.lines.map(l => ({
      product:     Number(l.product),
      qty_ordered: Number(l.qty_ordered),
      unit_price:  Number(l.unit_price)
    }))
  }
  try {
    await api.post('/sales-orders/', payload)
    await fetchData(); hideModal()
  } catch (err) {
    alert(err.response?.data?.detail || JSON.stringify(err.response?.data) || 'Failed to save sales order.')
  }
}

const shipSO = async (so) => {
  if (!confirm(t('salesOrder.confirmShip'))) return
  try {
    await shipSalesOrder(so.id)
    await fetchData()
  } catch (err) {
    console.error(err)
    alert(err.response?.data?.detail || 'Failed to ship sales order.')
  }
}

const returnSO = async (so) => {
  if (!confirm(t('salesOrder.confirmReturn'))) return
  try {
    await returnSalesOrder(so.id)
    await fetchData()
  } catch (err) {
    console.error(err)
    alert(err.response?.data?.detail || 'Failed to return sales order.')
  }
}
</script>